import React from 'react';

import { ArticleBlock } from '@lumapps/articles/components/ArticleBlock';
import { currentLanguageSelector } from '@lumapps/languages';
import { Theme } from '@lumapps/lumx/react';
import { isPageNode, isSystemPageNode, NavigationItemType, NavigationMapItem } from '@lumapps/navigation/types';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, TranslatableObject, translateFromApiV2Format } from '@lumapps/translations';

import { useNavigationAdminConfiguration } from '../../context/NavigationAdminConfigurationContext';
import { useNavigationAdminVisibleByReference } from '../../context/NavigationAdminVisibleByReferenceContext';
import { getNavigationMapNodeTitle } from '../../utils/nodes';
import { ContentNavigationItemDetails } from './ContentNavigationItemDetails';
import { NoPageNavigationItemDetails } from './NoPageNavigationItemDetails';
import { SystemPageNavigationItemDetails } from './SystemPageNavigationItemDetails';
import { UnavailableNavigationItemDetails } from './UnavailableNavigationItemDetails';

export interface NavigationItemDetailsParams {
    /** The Node to display. */
    node: NavigationMapItem;
    /** Whether the component is disabled */
    isDisabled?: boolean;
    /** Whether the node is unavailable (references a resource but we don't have info on the resource */
    isNodeUnavailable?: boolean;
    /** Whether we should force display the resource data for the title and visibility instead of the node's. Defaults false */
    displayPageReferenceData?: boolean;
    /** Theme. */
    theme?: Theme;
}

/** The Details of the Navigation Item. Used in the main page and in the details dialog for type Page nodes */
export const NavigationItemDetails = ({
    node,
    isDisabled,
    isNodeUnavailable,
    displayPageReferenceData = false,
    theme = Theme.light,
}: NavigationItemDetailsParams) => {
    const pageId = node.pageReference?.id;
    const pageType = node.pageReference?.type;

    const currentLanguage = useSelector(currentLanguageSelector);
    const { fetchAtInstanceLevel, availableEditableNodeFields } = useNavigationAdminConfiguration();
    const visibleByGroupReferences = useNavigationAdminVisibleByReference();

    // Unavailable node (resource is deleted or not synced yet)
    if (isNodeUnavailable) {
        return <UnavailableNavigationItemDetails pageType={pageType} />;
    }

    const titleToUse = displayPageReferenceData ? node.title : getNavigationMapNodeTitle(node, currentLanguage);
    const translatedTitle = translateFromApiV2Format((titleToUse || {}) as TranslatableObject);

    const nodeVisibilityFeeds =
        visibleByGroupReferences && node.visibleBy && node.visibleBy.length > 0
            ? node.visibleBy.map((visibleById: string) => ({
                  id: visibleById,
                  name: visibleByGroupReferences[visibleById] || GLOBAL.UNAVAILABLE_GROUP,
              }))
            : undefined;

    // If we fetch at instance level, do not pass the `name` from node.siteReference so the block won't display the instance source
    const instance = fetchAtInstanceLevel
        ? {
              ...node.siteReference,
              name: '',
          }
        : node.siteReference;

    // System Pages
    if (isSystemPageNode(node) && node.systemPageReference?.type) {
        return (
            <SystemPageNavigationItemDetails
                displayVisibility={Boolean(availableEditableNodeFields?.visibility)}
                node={node}
                isDisabled={isDisabled}
                nodeVisibilityFeeds={nodeVisibilityFeeds}
                titleToUse={titleToUse}
                theme={theme}
            />
        );
    }

    // Content page.
    if (
        isPageNode(node) &&
        pageType &&
        [
            NavigationItemType.CONTENT,
            NavigationItemType.COMMUNITY,
            NavigationItemType.CUSTOM_LIST,
            NavigationItemType.DIRECTORY,
            NavigationItemType.USER_DIRECTORY,
        ].includes(pageType)
    ) {
        return (
            <ContentNavigationItemDetails
                instance={instance}
                node={node}
                pageType={pageType}
                pageId={pageId}
                translatedTitle={translatedTitle}
                visibleByGroupReferences={visibleByGroupReferences}
                nodeVisibilityFeeds={nodeVisibilityFeeds}
                displayPageReferenceData={displayPageReferenceData}
                isDisabled={isDisabled}
            />
        );
    }

    // Article page.
    if (isPageNode(node) && pageType === NavigationItemType.ARTICLE) {
        return (
            <ArticleBlock
                article={{
                    id: pageId,
                    title: translatedTitle,
                }}
                variant="small"
                isDisabled={isDisabled}
            />
        );
    }

    // Nodes with no linked page (link, section & generic)
    return (
        <NoPageNavigationItemDetails
            node={node}
            nodeVisibilityFeeds={nodeVisibilityFeeds}
            titleToUse={titleToUse}
            translatedTitle={translatedTitle}
            currentLanguage={currentLanguage}
            isDisabled={isDisabled}
            displayVisibility={Boolean(availableEditableNodeFields?.visibility)}
            theme={theme}
        />
    );
};

NavigationItemDetails.displayName = 'NavigationItemDetails';
